// src/components/settings/LeadNotificationSettings.js
import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Bell, Mail, Loader2, CheckCircle } from 'lucide-react';

export default function LeadNotificationSettings({ currentUserProfile }) {
    const orgId = currentUserProfile?.organizationId;

    const [enabled, setEnabled]   = useState(true);
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [saved, setSaved]       = useState(false);

    useEffect(() => {
        if (!orgId) return;
        const unsub = onSnapshot(doc(db, 'organizations', orgId), (snap) => {
            if (snap.exists()) {
                // Default to true if field doesn't exist yet
                const val = snap.data().newLeadEmailEnabled;
                setEnabled(val === undefined ? true : val);
            }
            setLoading(false);
        });
        return unsub;
    }, [orgId]);

    const handleToggle = async (newVal) => {
        if (!orgId) return;
        setEnabled(newVal);
        setSaving(true);
        setSaved(false);
        try {
            await updateDoc(doc(db, 'organizations', orgId), {
                newLeadEmailEnabled: newVal,
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            console.error('Failed to save lead notification setting:', err);
            setEnabled(!newVal); // revert
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center gap-2 text-gray-400 text-sm">
                <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
        );
    }

    return (
        <div className="p-6 max-w-2xl space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1">Lead Notifications</h3>
                <p className="text-sm text-gray-500">
                    Control whether you receive an email when a new lead comes in — from your booking page, customer portal, or anywhere else.
                </p>
            </div>

            {/* Main toggle card */}
            <div className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg mt-0.5 ${enabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                            <Bell size={18} />
                        </div>
                        <div>
                            <p className="font-semibold text-gray-800 text-sm">Email me when a new lead arrives</p>
                            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                                Admins and Owners are notified immediately. If a lead is assigned to a specific team member, they'll also receive the email.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => handleToggle(!enabled)}
                        disabled={saving}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                            enabled ? 'bg-blue-600' : 'bg-gray-300'
                        } ${saving ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                        <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                                enabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                        />
                    </button>
                </div>
            </div>

            {/* What the email includes */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                    <Mail size={15} className="text-gray-500" />
                    <p className="text-sm font-semibold text-gray-700">What's included in the email</p>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                    {[
                        "Customer name, email, phone, and address",
                        "Service requested and any notes they left",
                        "Where the lead came from (booking page, portal, etc.)",
                        "Who the lead is assigned to",
                        "A direct link to view and action the lead in Spatchy",
                    ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                            <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                            {item}
                        </li>
                    ))}
                </ul>
            </div>

            {/* Who gets notified */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800 leading-relaxed">
                <strong>Who receives the email:</strong> All users with an Owner or Admin role are notified.
                If the lead is assigned to a specific team member (e.g. via the customer portal), that person also receives it — even if they're not an Admin.
                Duplicate emails to the same address are suppressed automatically.
            </div>

            {saved && (
                <div className="flex items-center gap-2 text-green-700 text-sm font-medium animate-in fade-in slide-in-from-bottom-2">
                    <CheckCircle size={16} /> Saved
                </div>
            )}
        </div>
    );
}